<template>
  <div class="dashboard" v-loading="loading">
    <div class="btn_agendar">
      <Button :icon="Plus" tag="router-link" :to="{ path: '/schedule', query: { new: '1' } }" plain class="btn">
        Novo agendamento
      </Button>
      <el-tooltip
        content="A exportação traz os resultados conforme o filtro de empresa e período aplicado no momento."
        placement="bottom"
        effect="dark"
      >
        <el-dropdown trigger="click" placement="bottom-end" @command="exportDashboard">
          <Button :icon="Download" plain class="btn" :loading="Boolean(exporting)" :disabled="Boolean(exporting)">
            Exportar
          </Button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="xlsx">Exportar Excel</el-dropdown-item>
              <el-dropdown-item command="pdf">Exportar PDF</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </el-tooltip>
    </div>

    <section v-if="onAirAnomaly" class="onair-alert">
      <el-icon class="onair-alert-icon"><WarningFilled /></el-icon>
      <div>
        <strong>Mais de 1 agendamento no ar na mesma cidade.</strong>
        Cidade(s) em conflito: {{ conflictingCitiesLabel }}. Entre em contato com o time Datacasting.
        <ul class="onair-alert-list">
          <li v-for="item in conflictingOnAirItems" :key="item.id">
            {{ item.title }} — {{ item.city }} ({{ item.hour }}) · ID: {{ item.id }}
          </li>
        </ul>
      </div>
    </section>

    <section class="panel onair-hero">
      <h2 class="panel-title">Agendamentos no ar</h2>
      <div v-if="onAirList.length" class="onair-list">
        <div v-for="item in onAirList" :key="item.id" class="onair-details">
          <span class="onair-dot" />
          <div class="onair-info">
            <span class="onair-title">{{ item.title }}</span>
            <span class="onair-meta">
              {{ item.city }} · cliente {{ item.businessClient }} · no ar desde {{ item.date }} {{ item.hour }}
            </span>
            <span class="onair-id">ID: {{ item.id }}</span>
          </div>
        </div>
      </div>
      <div v-else class="onair-empty">
        Nenhum agendamento no ar agora.
      </div>
    </section>

    <section class="filters-row">
      <div v-if="showBusinessClientFilter" class="filter-group">
        <span class="filter-label">Empresa</span>
        <el-select
          v-model="selectedClient"
          placeholder="Selecione a empresa"
          clearable
          class="client-filter app-select"
          @change="handleClientChange"
        >
          <el-option label="Todos" :value="null" />
          <el-option
            v-for="client in effectiveClients"
            :key="client"
            :label="client"
            :value="client"
          />
        </el-select>
      </div>

      <div class="filter-group">
        <span class="filter-label">Filtros rápidos</span>
        <div class="date-shortcuts">
          <button
            v-for="shortcut in DATE_SHORTCUTS"
            :key="shortcut.key"
            type="button"
            class="shortcut-btn"
            :class="{ 'shortcut-btn--active': activeShortcut === shortcut.key }"
            @click="applyShortcut(shortcut.key)"
          >
            {{ shortcut.label }}
          </button>
        </div>
      </div>

      <div class="filter-group">
        <span class="filter-label">Data</span>
        <VDatePicker
          :key="`range-${pickerResetKey}`"
          v-model="dateRange"
          mode="date"
          is-range
          :locale="'pt-BR'"
          class="vc-picker vc-picker--filter"
        >
          <template #default="{ togglePopover, inputValue }">
            <div class="vc-input-wrap" @click="togglePopover">
              <button
                type="button"
                class="vc-input-trigger"
                aria-label="Selecionar data ou intervalo"
                @click.stop="togglePopover"
              >
                <el-icon class="vc-input-icon"><Calendar /></el-icon>
              </button>
              <input
                class="vc-input"
                :value="formatDateRangeDisplay(inputValue)"
                readonly
                placeholder="Selecione"
              />
              <button
                v-if="dateRange.start || dateRange.end"
                type="button"
                class="vc-input-clear"
                aria-label="Limpar data"
                @click.stop="clearDateRange"
              >
                ×
              </button>
            </div>
          </template>
        </VDatePicker>
      </div>

      <div class="filter-group">
        <span class="filter-label">&nbsp;</span>
        <button type="button" class="clear-filters-btn" @click="clearFilters">Limpar filtros</button>
      </div>
    </section>

    <p class="period-label">Exibindo dados de {{ periodMetrics.periodLabel }}</p>

    <section class="kpi-row">
      <div v-for="tile in KPI_TILES" :key="tile.key" class="kpi-tile">
        <span class="kpi-label">
          {{ tile.label }}
          <el-tooltip
            :content="tile.tooltip"
            placement="top"
            effect="dark"
            popper-class="kpi-tooltip-popper"
          >
            <el-icon class="kpi-info-icon"><InfoFilled /></el-icon>
          </el-tooltip>
        </span>
        <span class="kpi-value">{{ kpis[tile.key].value }}{{ tile.valueSuffix }}</span>
        <span class="kpi-delta" :class="deltaClass(kpis[tile.key].deltaPct)">
          <el-icon><component :is="deltaIcon(kpis[tile.key].deltaPct)" /></el-icon>
          {{ Math.abs(kpis[tile.key].deltaPct) }}{{ tile.deltaUnit }} vs período anterior
        </span>
      </div>
      <ConfirmationDelayPanel :from="periodFrom" :to="periodTo" :business-client="activeClientFilter" />
    </section>

    <section class="panels-row">
      <div class="panel panel--wide">
        <h2 class="panel-title">{{ volumeChartTitle }}</h2>
        <div class="volume-chart-wrap">
          <svg
            class="volume-chart"
            :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
            preserveAspectRatio="none"
            role="img"
            aria-label="Volume de agendamentos no período selecionado"
          >
            <line
              v-for="tick in gridTicks"
              :key="tick.y"
              x1="0"
              :y1="tick.y"
              :x2="chartWidth"
              :y2="tick.y"
              class="grid-line"
            />
            <path :d="areaPath" class="area-fill" />
            <polyline :points="linePoints" class="volume-line" />
            <circle
              v-for="(p, i) in volumePoints"
              :key="i"
              :cx="p.x"
              :cy="p.y"
              r="8"
              class="hover-target"
              @mouseenter="hoveredIndex = i"
              @mouseleave="hoveredIndex = null"
            />
            <circle
              v-for="(p, i) in volumePoints"
              :key="`dot-${i}`"
              :cx="p.x"
              :cy="p.y"
              r="4"
              class="volume-dot"
              :class="{ 'volume-dot--active': hoveredIndex === i }"
            />
          </svg>
          <div
            v-if="hoveredIndex !== null"
            class="chart-tooltip"
            :style="tooltipStyle"
          >
            <strong>{{ volumeSeries[hoveredIndex].count }}</strong>
            agendamentos<br />
            {{ volumeSeries[hoveredIndex].label }}
          </div>
        </div>
        <div class="volume-labels">
          <span
            v-for="(p, i) in volumeSeries"
            :key="i"
            class="volume-label"
            :class="{ 'volume-label--muted': i % 2 !== 0 }"
          >{{ p.label }}</span>
        </div>
      </div>

      <div class="panel">
        <h2 class="panel-title">Configurações do receptor</h2>
        <ul class="metric-list">
          <li class="metric-row">
            <span class="metric-label">{{ downloadWindowSummary.label }}</span>
            <span class="metric-value">{{ downloadWindowSummary.value }}</span>
          </li>
          <li class="metric-row">
            <span class="metric-label">Cenários de recebimento ativos</span>
            <span class="metric-value">{{ activeScenariosCount }}/{{ downloadScenarios.length }}</span>
          </li>
        </ul>
        <ul class="config-list">
          <li
            v-for="s in visibleScenarios"
            :key="`${s.business_client}-${s.download_scenario}`"
            class="config-row"
          >
            <div class="config-row-top">
              <span class="config-client">{{ s.business_client }} · cenário {{ s.download_scenario }}</span>
              <el-tag :type="s.active ? 'success' : 'info'" effect="light" size="small">
                {{ s.active ? "Ativo" : "Inativo" }}
              </el-tag>
            </div>
            <span class="config-code">{{ s.description }}</span>
          </li>
        </ul>
        <router-link
          v-if="hiddenScenariosCount > 0"
          :to="{ path: '/settings', query: { tab: 'scenarios' } }"
          class="config-more-link"
        >
          Ver todos
        </router-link>
      </div>
    </section>

    <section class="panel">
      <h2 class="panel-title">Status dos agendamentos no período</h2>
      <div class="status-cards">
        <div v-for="s in statusBreakdown" :key="s.key" class="status-card" :style="{ borderLeftColor: s.color }">
          <span class="status-card-label">
            <span class="legend-swatch" :style="{ backgroundColor: s.color }" />
            {{ s.label }}
          </span>
          <span class="status-card-value">{{ s.count }}</span>
          <span class="status-card-pct">{{ pct(s.count, statusTotal).toFixed(1) }}% do total</span>
        </div>
      </div>
      <div class="stacked-bar">
        <div
          v-for="s in statusBreakdown"
          :key="s.key"
          class="stacked-segment"
          :style="{ flexBasis: pct(s.count, statusTotal) + '%', backgroundColor: s.color }"
        >
          <span v-if="pct(s.count, statusTotal) > 12" class="segment-label">{{ s.count }}</span>
        </div>
      </div>
    </section>

    <section class="panel">
      <h2 class="panel-title">Mídias e recebimento (return-channel)</h2>
      <div class="media-layout">
        <ul class="metric-list media-metric-list">
          <li class="metric-row">
            <span class="metric-label">Tamanho médio por mídia</span>
            <span class="metric-value">{{ mediaProfile.avgSizeMB }} MB</span>
          </li>
          <li class="metric-row">
            <span class="metric-label">Mídias recebidas no período</span>
            <span class="metric-value">{{ receiptMetrics.receivedInPeriod }}</span>
          </li>
          <li class="metric-row">
            <span class="metric-label">Dispositivos únicos que confirmaram</span>
            <span class="metric-value">{{ receiptMetrics.uniqueDevices }}</span>
          </li>
          <li class="metric-row">
            <span class="metric-label">Armazenamento recebido no período</span>
            <span class="metric-value">{{ receiptMetrics.storageInPeriodGB }} GB</span>
          </li>
        </ul>
        <ul class="city-list media-device-list">
          <li v-for="d in receiptMetrics.topDevices" :key="d.device" class="city-row">
            <span class="city-name">{{ d.device }}</span>
            <span class="city-track">
              <span class="city-fill" :style="{ width: pct(d.count, deviceMax) + '%' }" />
            </span>
            <span class="city-count">{{ d.count }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section class="panels-row">
      <div class="panel">
        <h2 class="panel-title">Agendamentos por cidade</h2>
        <ul class="city-list">
          <li v-for="c in topCities" :key="c.city" class="city-row">
            <span class="city-name">{{ c.label }}</span>
            <span class="city-track">
              <span class="city-fill" :style="{ width: pct(c.count, cityMax) + '%' }" />
            </span>
            <span class="city-count">{{ c.count }}</span>
          </li>
        </ul>
      </div>

      <div class="panel panel--wide">
        <h2 class="panel-title">Próximos agendamentos</h2>
        <table class="upcoming-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Empresa</th>
              <th>Cidade</th>
              <th>Data</th>
              <th>Hora</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in upcoming" :key="i">
              <td>{{ item.title }}</td>
              <td>{{ item.businessClient }}</td>
              <td>{{ item.city }}</td>
              <td>{{ item.date }}</td>
              <td>{{ item.hour }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import { ElIcon, ElMessage } from "element-plus";
import { ArrowUp, ArrowDown, WarningFilled, Calendar, InfoFilled, Plus, Download } from "@element-plus/icons-vue";
import { config } from "../../config";
import { loadBusinessClients } from "../../services/businessClients";
import { clientViewState } from "../../utils/clientView";
import { exportScheduleExcel, exportSchedulePdf } from "../../utils/dashboardExport";
import Button from "../Component/Button.vue";
import ConfirmationDelayPanel from "../Dashboard/ConfirmationDelayPanel.vue";

const CHART_WIDTH = 640;
const CHART_HEIGHT = 180;
const CHART_TOP_PADDING = 20;

const CITY_LABELS = {
  sp: "São Paulo",
  rj: "Rio de Janeiro",
  bh: "Belo Horizonte",
  df: "Distrito Federal",
};

const STATUS_META = {
  scheduled: { label: "Agendado", color: "#9aa4af" },
  onair: { label: "No ar", color: "#3abef9" },
  finished: { label: "Finalizado", color: "#0ca30c" },
  error: { label: "Erro", color: "#e15757" },
  canceled: { label: "Cancelado", color: "#6b7280" },
};
const STATUS_ORDER = ["scheduled", "onair", "finished", "error", "canceled"];

const DATE_SHORTCUTS = [
  { key: "today", label: "Hoje" },
  { key: "month", label: "Mês atual" },
  { key: "last30", label: "Últimos 30 dias" },
  { key: "year", label: "Ano atual" },
];

const KPI_TILES = [
  {
    key: "totalInPeriod",
    label: "Agendamentos no período",
    valueSuffix: "",
    deltaUnit: "%",
    tooltip: "Total de agendamentos no período selecionado, somando todos os status (agendado, no ar, finalizado, erro e cancelado).",
  },
  {
    key: "finishedInPeriod",
    label: "Finalizados no período",
    valueSuffix: "",
    deltaUnit: "%",
    tooltip: "Quantidade de agendamentos com status \"Finalizado\" dentro do período selecionado.",
  },
  {
    key: "successRate",
    label: "Taxa de sucesso",
    valueSuffix: "%",
    deltaUnit: "pp",
    tooltip: "Percentual de agendamentos finalizados com sucesso. Variação em pontos percentuais (pp).",
  },
];

// O dashboard é um resumo, não substitui a tela de Configurações - mostra só os
// primeiros cenários e linka pra lá quando tem mais que isso.
const SCENARIO_LIST_LIMIT = 6;

const EMPTY_PERIOD_METRICS = {
  periodLabel: "",
  granularity: "day",
  kpis: {
    totalInPeriod: { value: 0, deltaPct: 0 },
    finishedInPeriod: { value: 0, deltaPct: 0 },
    successRate: { value: 0, deltaPct: 0 },
  },
  volumeSeries: [{ label: "", count: 0 }],
  statusBreakdown: STATUS_ORDER.map((key) => ({ key, ...STATUS_META[key], count: 0 })),
  mediaProfile: { avgSizeMB: 0 },
  receiptMetrics: { receivedInPeriod: 0, uniqueDevices: 0, storageInPeriodGB: 0, topDevices: [] },
  topCities: [],
};

function toISODateOnly(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateLabel(date) {
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// series[].label vem em formato "máquina" do backend (2026-08-13 / ...T16:00 / 2026-08) -
// a formatação pt-BR pra exibição é responsabilidade do front.
function formatSeriesLabel(rawLabel, granularity) {
  const value = String(rawLabel || "");
  if (granularity === "hour") {
    const match = value.match(/T(\d{2}):/);
    return match ? `${match[1]}h` : value;
  }
  if (granularity === "month") {
    const [year, month] = value.split("-");
    if (!year || !month) return value;
    return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString("pt-BR", {
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    });
  }
  // day ou week: "YYYY-MM-DD" (bucket semanal usa a data de início da semana)
  const [, month, day] = value.split(/[-T]/);
  return day && month ? `${day}/${month}` : value;
}

// Mesmo formato usado em ListSchedules.vue pra manter a hora consistente entre as telas.
function splitStartDate(startDate) {
  const [date, hour] = new Date(startDate)
    .toLocaleString("pt-BR", { timezone: "UTC" })
    .split(", ");
  return { date, hour };
}

function mapScheduleItem(item) {
  const { date, hour } = splitStartDate(item.start_date);
  const cities = item.cities || [];
  return {
    id: item.id,
    title: item.title,
    businessClient: item.business_client,
    cities, // códigos crus (sp/rj/bh/df) - usados pra detectar conflito por cidade
    city: cities.map((c) => CITY_LABELS[c] || c).join(", "),
    date,
    hour,
  };
}

function extractErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return "Verifique os filtros informados.";
  return detail || "Não foi possível carregar os dados do dashboard.";
}

function pctDelta(current, previous) {
  if (!previous) return 0;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

function buildPeriodMetrics(current, previous, receipts, dateFrom, dateTo) {
  const granularity = current?.period?.granularity || "day";
  const volumeSeries = (current?.series || []).map((point) => ({
    label: formatSeriesLabel(point.label, granularity),
    count: point.count,
  }));

  const statusBreakdown = STATUS_ORDER.map((key) => ({
    key,
    ...STATUS_META[key],
    count: current?.by_status?.[key] || 0,
  }));

  const topCities = (current?.top_cities || []).map((c) => ({
    city: c.city,
    label: CITY_LABELS[c.city] || c.city,
    count: c.count,
  }));

  const currentKpis = current?.kpis || {};
  const previousKpis = previous?.kpis || {};

  return {
    periodLabel: `${formatDateLabel(dateFrom)} – ${formatDateLabel(dateTo)}`,
    granularity,
    kpis: {
      totalInPeriod: {
        value: currentKpis.total || 0,
        deltaPct: pctDelta(currentKpis.total, previousKpis.total),
      },
      finishedInPeriod: {
        value: currentKpis.finished || 0,
        deltaPct: pctDelta(currentKpis.finished, previousKpis.finished),
      },
      successRate: {
        value: Math.round((currentKpis.success_rate || 0) * 10) / 10,
        deltaPct: Math.round(((currentKpis.success_rate || 0) - (previousKpis.success_rate || 0)) * 10) / 10,
      },
    },
    volumeSeries: volumeSeries.length ? volumeSeries : EMPTY_PERIOD_METRICS.volumeSeries,
    statusBreakdown,
    mediaProfile: { avgSizeMB: receipts.avgSizeMB },
    receiptMetrics: {
      receivedInPeriod: receipts.receivedInPeriod,
      uniqueDevices: receipts.uniqueDevices,
      storageInPeriodGB: receipts.storageInPeriodGB,
      topDevices: receipts.topDevices,
    },
    topCities,
  };
}

export default {
  name: "DashboardPage",
  components: { ElIcon, WarningFilled, Calendar, InfoFilled, Button, ConfirmationDelayPanel },
  data() {
    return {
      onAirList: [],
      upcoming: [],
      downloadWindowsRaw: [],
      downloadScenariosRaw: [],
      periodMetrics: EMPTY_PERIOD_METRICS,
      clientViewState,
      availableClients: [],
      selectedClient: null,
      dateRange: { start: null, end: null },
      pickerResetKey: 0,
      hoveredIndex: null,
      loading: false,
      periodReloadQueued: false,
      suppressPeriodWatch: false,
      exporting: null,
    };
  },
  computed: {
    Plus: () => Plus,
    Download: () => Download,
    // Conflito de verdade é por cidade: campanhas de clientes/cidades diferentes
    // podem ficar no ar ao mesmo tempo sem problema. O que não pode é a MESMA
    // cidade ter mais de 1 agendamento no ar simultaneamente.
    onAirCityConflicts() {
      const counts = new Map();
      this.onAirList.forEach((item) => {
        item.cities.forEach((c) => counts.set(c, (counts.get(c) || 0) + 1));
      });
      return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([city]) => city));
    },
    onAirAnomaly() {
      return this.onAirCityConflicts.size > 0;
    },
    conflictingCitiesLabel() {
      return [...this.onAirCityConflicts].map((c) => CITY_LABELS[c] || c).join(", ");
    },
    conflictingOnAirItems() {
      return this.onAirList.filter((item) => item.cities.some((c) => this.onAirCityConflicts.has(c)));
    },
    // A empresa só é filtrável na página quando "Visualizar como" (header) está em
    // "Administrador" - se já tem um client_view específico escolhido, ou o usuário
    // só tem 1 empresa autorizada (cliente comum), não faz sentido um segundo seletor.
    effectiveClients() {
      return this.clientViewState.selectedClient
        ? [this.clientViewState.selectedClient]
        : this.availableClients;
    },
    effectiveBusinessClient() {
      return this.effectiveClients.length === 1 ? this.effectiveClients[0] : null;
    },
    showBusinessClientFilter() {
      return this.effectiveClients.length > 1;
    },
    // Único ponto de decisão de qual business_client usar nas requests/filtros:
    // a seleção local da página quando ela tem seletor próprio, senão o escopo
    // efetivo herdado do "Visualizar como" global.
    activeClientFilter() {
      return this.showBusinessClientFilter ? this.selectedClient : this.effectiveBusinessClient;
    },
    periodFrom() {
      return toISODateOnly(this.dateRange?.start);
    },
    periodTo() {
      return toISODateOnly(this.dateRange?.end);
    },
    kpis() {
      return this.periodMetrics.kpis;
    },
    volumeSeries() {
      return this.periodMetrics.volumeSeries;
    },
    statusBreakdown() {
      return this.periodMetrics.statusBreakdown;
    },
    mediaProfile() {
      return this.periodMetrics.mediaProfile;
    },
    receiptMetrics() {
      return this.periodMetrics.receiptMetrics;
    },
    topCities() {
      return this.periodMetrics.topCities;
    },
    downloadWindows() {
      return this.filterByClient(this.downloadWindowsRaw);
    },
    downloadScenarios() {
      return this.filterByClient(this.downloadScenariosRaw);
    },
    visibleScenarios() {
      return this.downloadScenarios.slice(0, SCENARIO_LIST_LIMIT);
    },
    hiddenScenariosCount() {
      return Math.max(0, this.downloadScenarios.length - SCENARIO_LIST_LIMIT);
    },
    activeWindowsCount() {
      return this.downloadWindows.filter((w) => w.active).length;
    },
    // Cada empresa só tem 1 identificador próprio - mostrar o número configurado
    // é mais útil que uma contagem "1/1". A contagem só volta a fazer sentido na
    // visão "Todos" (admin), quando há mais de um cliente no escopo.
    downloadWindowSummary() {
      const windows = this.downloadWindows;
      if (windows.length === 0) {
        return { label: "Identificador de cliente ativo", value: "—" };
      }
      if (windows.length === 1) {
        const w = windows[0];
        return {
          label: "Identificador de cliente ativo",
          value: w.active ? `${w.download_window}` : `${w.download_window} (inativo)`,
        };
      }
      return {
        label: "Identificadores de cliente ativos",
        value: `${this.activeWindowsCount}/${windows.length}`,
      };
    },
    activeScenariosCount() {
      return this.downloadScenarios.filter((s) => s.active).length;
    },
    volumeChartTitle() {
      const titles = {
        hour: "Agendamentos por hora (hoje)",
        day: "Agendamentos por dia",
        week: "Agendamentos por semana",
        month: "Agendamentos por mês",
      };
      return titles[this.periodMetrics.granularity] || "Agendamentos no período";
    },
    activeShortcut() {
      const from = toISODateOnly(this.dateRange?.start);
      const to = toISODateOnly(this.dateRange?.end);
      if (!from || !to) return null;
      const shortcuts = ["today", "month", "last30", "year"];
      return shortcuts.find((key) => {
        const range = this.shortcutRange(key);
        return toISODateOnly(range.from) === from && toISODateOnly(range.to) === to;
      }) || null;
    },
    DATE_SHORTCUTS: () => DATE_SHORTCUTS,
    KPI_TILES: () => KPI_TILES,
    chartWidth: () => CHART_WIDTH,
    chartHeight: () => CHART_HEIGHT,
    // min/max num único pass sobre volumeSeries, em vez de um Math.max/min
    // separado cada um mapeando o array de novo.
    volumeRange() {
      return this.volumeSeries.reduce(
        (range, p) => ({ min: Math.min(range.min, p.count), max: Math.max(range.max, p.count) }),
        { min: Infinity, max: -Infinity }
      );
    },
    volumePoints() {
      const n = this.volumeSeries.length;
      const { min, max } = this.volumeRange;
      const range = max - min || 1;
      const plotHeight = CHART_HEIGHT - CHART_TOP_PADDING;
      return this.volumeSeries.map((p, i) => {
        const x = n > 1 ? (i / (n - 1)) * CHART_WIDTH : CHART_WIDTH / 2;
        const y = CHART_HEIGHT - ((p.count - min) / range) * plotHeight;
        return { x, y };
      });
    },
    linePoints() {
      return this.volumePoints.map((p) => `${p.x},${p.y}`).join(" ");
    },
    areaPath() {
      const pts = this.volumePoints;
      if (!pts.length) return "";
      const first = pts[0];
      const last = pts[pts.length - 1];
      const top = pts.map((p) => `${p.x},${p.y}`).join(" L ");
      return `M ${first.x},${CHART_HEIGHT} L ${top} L ${last.x},${CHART_HEIGHT} Z`;
    },
    gridTicks() {
      const steps = 4;
      return Array.from({ length: steps }, (_, i) => ({
        y: (i / (steps - 1)) * (CHART_HEIGHT - CHART_TOP_PADDING),
      }));
    },
    statusTotal() {
      return this.statusBreakdown.reduce((sum, s) => sum + s.count, 0) || 1;
    },
    cityMax() {
      return Math.max(...this.topCities.map((c) => c.count), 1);
    },
    deviceMax() {
      return Math.max(...this.receiptMetrics.topDevices.map((d) => d.count), 1);
    },
    tooltipStyle() {
      if (this.hoveredIndex === null) return {};
      const p = this.volumePoints[this.hoveredIndex];
      const leftPct = (p.x / CHART_WIDTH) * 100;
      const topPct = (p.y / CHART_HEIGHT) * 100;
      return {
        left: `${leftPct}%`,
        top: `${topPct}%`,
      };
    },
  },
  watch: {
    dateRange: {
      deep: true,
      handler() {
        if (!this.suppressPeriodWatch) this.scheduleReloadPeriod();
      },
    },
    "clientViewState.selectedClient"() {
      this.applyClientScope();
      this.loadAll();
    },
  },
  methods: {
    pct(count, total) {
      return (count / total) * 100;
    },
    deltaClass(deltaPct) {
      return deltaPct >= 0 ? "kpi-delta--up" : "kpi-delta--down";
    },
    deltaIcon(deltaPct) {
      return deltaPct >= 0 ? ArrowUp : ArrowDown;
    },
    shortcutRange(key) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (key === "month") {
        return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
      }
      if (key === "last30") {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return { from, to: today };
      }
      if (key === "year") {
        return { from: new Date(today.getFullYear(), 0, 1), to: today };
      }
      return { from: today, to: today };
    },
    applyShortcut(key) {
      const range = this.shortcutRange(key);
      this.dateRange = { start: range.from, end: range.to };
      this.pickerResetKey += 1;
    },
    clearFilters() {
      this.applyShortcut("last30");
    },
    clearDateRange() {
      this.dateRange = { start: null, end: null };
      this.pickerResetKey += 1;
    },
    formatDateRangeDisplay(inputValue) {
      if (!inputValue?.start) return "";
      if (!inputValue.end || inputValue.end === inputValue.start) return inputValue.start;
      return `${inputValue.start} – ${inputValue.end}`;
    },
    scheduleReloadPeriod() {
      if (this.periodReloadQueued) return;
      this.periodReloadQueued = true;
      this.$nextTick(() => {
        this.periodReloadQueued = false;
        this.reloadPeriod();
      });
    },
    businessClientParams() {
      return this.activeClientFilter ? { business_client: this.activeClientFilter } : {};
    },
    filterByClient(rows) {
      return this.activeClientFilter ? rows.filter((r) => r.business_client === this.activeClientFilter) : rows;
    },
    // Mesma regra de start_date_on vs start_date_from/to usada em ListSchedules.vue,
    // pra manter o filtro do export idêntico ao que a API já espera.
    scheduleExportParams() {
      const params = { ...this.businessClientParams() };
      const from = toISODateOnly(this.dateRange?.start);
      const to = toISODateOnly(this.dateRange?.end);
      if (from && to && from === to) {
        params.start_date_on = from;
      } else {
        if (from) params.start_date_from = from;
        if (to) params.start_date_to = to;
      }
      return params;
    },
    async fetchAllSchedulesForExport() {
      const pageSize = 100;
      const baseParams = this.scheduleExportParams();
      const items = [];
      let page = 1;
      let total = 0;
      do {
        const { data } = await axios.get(`${config.API}/v1/schedule/`, {
          params: { page, page_size: pageSize, ...baseParams },
        });
        const pageItems = data.items || [];
        items.push(...pageItems);
        total = data.total || items.length;
        if (pageItems.length === 0) break;
        page += 1;
      } while (items.length < total);
      return items;
    },
    mapExportRow(item) {
      const { date, hour } = splitStartDate(item.start_date);
      const cities = (item.cities || []).map((c) => CITY_LABELS[c] || c).join(", ");
      return {
        title: item.title,
        businessClient: item.business_client,
        city: cities,
        dateTime: `${date} ${hour}`,
        status: STATUS_META[item.status]?.label || item.status,
      };
    },
    // Mesmos dados do painel "Configurações do receptor", já filtrados por cliente.
    exportScenarioConfig() {
      return {
        windows: this.downloadWindows.map((w) => ({
          businessClient: w.business_client,
          identifier: w.download_window,
          status: w.active ? "Ativo" : "Inativo",
        })),
        scenarios: this.downloadScenarios.map((s) => ({
          businessClient: s.business_client,
          scenario: s.download_scenario,
          description: s.description,
          status: s.active ? "Ativo" : "Inativo",
        })),
      };
    },
    // Mesmos números exibidos nos painéis de KPIs, status e cidades do Dashboard.
    exportSummaryData() {
      return {
        periodLabel: this.periodMetrics.periodLabel,
        kpis: KPI_TILES.map((tile) => {
          const kpi = this.kpis[tile.key];
          const sign = kpi.deltaPct >= 0 ? "+" : "";
          return {
            label: tile.label,
            value: `${kpi.value}${tile.valueSuffix}`,
            delta: `${sign}${kpi.deltaPct}${tile.deltaUnit}`,
          };
        }),
        statusBreakdown: this.statusBreakdown.map((s) => ({
          label: s.label,
          count: s.count,
          pct: `${this.pct(s.count, this.statusTotal).toFixed(1)}%`,
        })),
        topCities: this.topCities.map((c) => ({ label: c.label, count: c.count })),
      };
    },
    async exportDashboard(format) {
      if (this.exporting) return;
      this.exporting = format;
      try {
        const items = await this.fetchAllSchedulesForExport();
        if (items.length === 0) {
          ElMessage.warning("Não há agendamentos para exportar com os filtros atuais.");
          return;
        }
        const rows = items.map(this.mapExportRow);
        const media = { ...this.mediaProfile, ...this.receiptMetrics };
        const scenarioConfig = this.exportScenarioConfig();
        const summary = this.exportSummaryData();
        if (format === "xlsx") await exportScheduleExcel(rows, media, scenarioConfig, summary);
        else await exportSchedulePdf(rows, media, scenarioConfig, summary);
        ElMessage.success(`${rows.length} agendamentos exportados.`);
      } catch (e) {
        console.error("Erro ao exportar relatório do dashboard:", e);
        ElMessage.error(extractErrorMessage(e));
      } finally {
        this.exporting = null;
      }
    },
    // Espelha o applyClientScope de ListSchedules.vue: reage à troca do "Visualizar
    // como" global (header) recalculando o escopo efetivo desta página.
    applyClientScope() {
      this.selectedClient = this.effectiveBusinessClient;
    },
    async fetchBusinessClients() {
      try {
        this.availableClients = await loadBusinessClients();
        this.selectedClient = this.availableClients.length === 1 ? this.availableClients[0] : null;
      } catch (e) {
        console.error("Erro ao buscar clientes disponíveis:", e);
      }
    },
    async fetchDashboard() {
      const effectiveFrom = this.dateRange?.start;
      const effectiveTo = this.dateRange?.end;
      if (!effectiveFrom || !effectiveTo) return;
      try {
        const { data } = await axios.get(`${config.API}/v1/metrics/dashboard`, {
          params: {
            from: toISODateOnly(effectiveFrom),
            to: toISODateOnly(effectiveTo),
            ...this.businessClientParams(),
          },
        });
        this.onAirList = (data.realtime?.onair || []).map(mapScheduleItem);
        this.upcoming = (data.realtime?.upcoming || []).map(mapScheduleItem);
        this.downloadWindowsRaw = data.downloadWindows || [];
        this.downloadScenariosRaw = data.downloadScenarios || [];
        this.periodMetrics = buildPeriodMetrics(
          data.current,
          data.previous,
          data.receipts,
          effectiveFrom,
          effectiveTo
        );
      } catch (e) {
        console.error("Erro ao buscar dados do dashboard:", e);
        ElMessage.error(extractErrorMessage(e));
      }
    },
    async reloadPeriod() {
      this.loading = true;
      try {
        await this.fetchDashboard();
      } finally {
        this.loading = false;
      }
    },
    async loadAll() {
      this.loading = true;
      try {
        await this.fetchDashboard();
      } finally {
        this.loading = false;
      }
    },
    handleClientChange() {
      this.loadAll();
    },
  },
  async mounted() {
    this.suppressPeriodWatch = true;
    this.applyShortcut("last30");

    this.loading = true;
    // A lista de empresas atualiza o filtro sem bloquear os dados da tela.
    // fetchBusinessClients trata os erros; o backend valida o escopo pelo token.
    this.fetchBusinessClients();
    try {
      await this.fetchDashboard();
    } finally {
      this.loading = false;
      this.suppressPeriodWatch = false;
    }
  },
};
</script>

<style scoped>
@import "@/assets/css/fonts.css";

.dashboard {
  font-family: "Globo-Regular", sans-serif;
  color: #2b4054;
}

.btn_agendar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 25px;
  gap: 12px;
}

.btn_agendar :deep(.el-button.is-plain) {
  color: #3a6d8c;
  font-weight: 600;
  text-decoration: none;
}

.btn_agendar :deep(.el-button.is-plain .el-icon) {
  color: #3a6d8c;
}

.filters-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  row-gap: 14px;
  column-gap: 20px;
  margin-bottom: 10px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-label {
  color: #2b4054;
  font-size: 14px;
}

.client-filter {
  width: 220px;
}

.app-select :deep(.el-select__wrapper) {
  min-height: 40px;
  box-shadow: 0 0 0 1px #dcdfe6 inset;
}

.app-select :deep(.el-select__wrapper.is-hovering:not(.is-focused)),
.app-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 1px #3a6d8c inset;
}

.app-select :deep(.el-select__selected-item) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.app-select :deep(.el-select__placeholder) {
  color: #2b4054;
  font-family: "Globo-Regular", sans-serif;
}

.app-select :deep(.el-select__placeholder.is-transparent) {
  color: #9aa4af;
}

.app-select :deep(.el-select__caret) {
  color: #9aa4af;
}

.date-shortcuts {
  display: flex;
  gap: 6px;
  height: 40px;
  align-items: center;
}

.shortcut-btn {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #3a6d8c;
  font-size: 13px;
  font-family: "Globo-Regular", sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.15s, border-color 0.15s;
}

.shortcut-btn:hover {
  background-color: #f0f5f8;
  border-color: #3a6d8c;
}

.shortcut-btn--active {
  background-color: #3a6d8c;
  border-color: #3a6d8c;
  color: #fff;
}

.shortcut-btn--active:hover {
  background-color: #2f5972;
  border-color: #2f5972;
}

.clear-filters-btn {
  height: 40px;
  padding: 0 4px;
  border: none;
  background: transparent;
  color: #6f7b87;
  font-size: 13px;
  font-family: "Globo-Regular", sans-serif;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
}

.clear-filters-btn:hover {
  color: #3a6d8c;
}

.vc-picker--filter {
  width: 180px;
}

.vc-picker :deep(.vc-popover-content-wrapper) {
  left: 0 !important;
  transform: none !important;
  top: calc(100% + 4px) !important;
}

.vc-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 0 12px 0 0;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.vc-input-wrap:hover,
.vc-input-wrap:focus-within {
  border-color: #3a6d8c;
}

.vc-input-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  border: none;
  background: transparent;
  cursor: pointer;
}

.vc-input-icon {
  color: #9aa4af;
  flex-shrink: 0;
}

.vc-input {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 14px;
  font-family: "Globo-Regular", sans-serif;
  color: #2b4054;
  cursor: pointer;
}

.vc-input::placeholder {
  color: #9aa4af;
  font-family: "Globo-Regular", sans-serif;
}

.vc-input-clear {
  border: none;
  background: transparent;
  color: #9aa4af;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 8px 0 4px;
}

.period-label {
  margin: 0 0 20px;
  font-size: 13px;
  color: #6f7b87;
}

.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.kpi-tile {
  flex: 1 1 200px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #fff;
  border: 1px solid #e6edf5;
  border-radius: 12px;
  padding: 18px 20px;
}

.kpi-label {
  font-size: 14px;
  color: #6f7b87;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.kpi-info-icon {
  font-size: 13px;
  color: #9aa4af;
  cursor: help;
}

:global(.kpi-tooltip-popper) {
  max-width: 240px;
}

.kpi-value {
  font-size: 32px;
  font-family: "Globo-Bold", sans-serif;
  color: #2b4054;
}

.kpi-delta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  width: fit-content;
}

.kpi-delta--up {
  color: #0ca30c;
}

.kpi-delta--down {
  color: #e15757;
}

.onair-alert {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: rgba(225, 87, 87, 0.08);
  border: 1px solid #e15757;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #2b4054;
}

.onair-alert-icon {
  color: #e15757;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
}

.onair-alert-list {
  margin: 8px 0 0;
  padding-left: 18px;
}

.onair-hero {
  margin-bottom: 20px;
}

.onair-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.onair-details {
  display: flex;
  align-items: center;
  gap: 14px;
}

.onair-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #3abef9;
  box-shadow: 0 0 0 4px rgba(58, 190, 249, 0.2);
  flex-shrink: 0;
}

.onair-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.onair-title {
  font-family: "Globo-Bold", sans-serif;
  font-size: 18px;
  color: #2b4054;
}

.onair-meta {
  font-size: 13px;
  color: #6f7b87;
}

.onair-id {
  font-size: 11px;
  font-family: monospace;
  color: #9aa4af;
}

.onair-empty {
  font-size: 14px;
  color: #6f7b87;
}

.metric-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.metric-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
}

.metric-label {
  color: #6f7b87;
}

.metric-value {
  font-family: "Globo-Bold", sans-serif;
  font-size: 15px;
  color: #2b4054;
}

.config-list {
  list-style: none;
  margin: 16px 0 0;
  padding: 12px 0 0;
  border-top: 1px solid #e6edf5;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 280px;
  overflow-y: auto;
}

.config-more-link {
  display: block;
  margin-top: 12px;
  font-size: 12px;
  color: #3a6d8c;
  text-decoration: underline;
}

.config-more-link:hover {
  color: #2f5972;
}

.config-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
}

.config-row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.config-client {
  font-family: "Globo-Bold", sans-serif;
  color: #2b4054;
  text-transform: capitalize;
}

.config-code {
  color: #6f7b87;
}

.media-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
}

.media-metric-list,
.media-device-list {
  flex: 1 1 260px;
}

.status-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 20px;
}

.status-card {
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  border-left: 3px solid;
  background: #f5f7f8;
  border-radius: 6px;
}

.status-card-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6f7b87;
}

.status-card-value {
  font-family: "Globo-Bold", sans-serif;
  font-size: 22px;
  color: #2b4054;
}

.status-card-pct {
  font-size: 12px;
  color: #9aa4af;
}

.panels-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
}

.panel {
  flex: 1 1 320px;
  background: #fff;
  border: 1px solid #e6edf5;
  border-radius: 12px;
  padding: 20px;
}

.panel--wide {
  flex: 2 1 420px;
}

.panel-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-family: "Globo-Bold", sans-serif;
  color: #3a6d8c;
}

.volume-chart-wrap {
  position: relative;
}

.volume-chart {
  width: 100%;
  height: 180px;
  display: block;
  overflow: visible;
}

.grid-line {
  stroke: #e6edf5;
  stroke-width: 1;
}

.area-fill {
  fill: #3a6d8c;
  opacity: 0.1;
  stroke: none;
}

.volume-line {
  fill: none;
  stroke: #3a6d8c;
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.volume-dot {
  fill: #3a6d8c;
  stroke: #fff;
  stroke-width: 2;
  opacity: 0;
}

.volume-dot--active {
  opacity: 1;
}

.hover-target {
  fill: transparent;
  cursor: pointer;
}

.chart-tooltip {
  position: absolute;
  transform: translate(-50%, calc(-100% - 10px));
  background: #2b4054;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
}

.volume-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
}

.volume-label {
  font-size: 11px;
  color: #6f7b87;
}

.volume-label--muted {
  visibility: hidden;
}

.stacked-bar {
  display: flex;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  gap: 2px;
}

.stacked-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2px;
}

.segment-label {
  color: #fff;
  font-size: 12px;
  font-family: "Globo-Bold", sans-serif;
}

.legend-swatch {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}

.city-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.city-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.city-name {
  width: 130px;
  flex-shrink: 0;
  font-size: 13px;
  color: #2b4054;
}

.city-track {
  flex: 1;
  height: 20px;
  background: #f5f7f8;
  border-radius: 0 4px 4px 0;
  overflow: hidden;
}

.city-fill {
  display: block;
  height: 100%;
  background: #3a6d8c;
  border-radius: 0 4px 4px 0;
}

.city-count {
  width: 40px;
  flex-shrink: 0;
  text-align: right;
  font-family: "Globo-Bold", sans-serif;
  font-size: 13px;
  color: #2b4054;
}

.upcoming-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.upcoming-table th {
  text-align: left;
  color: #6f7b87;
  font-weight: normal;
  padding: 0 8px 10px 0;
  border-bottom: 1px solid #e6edf5;
}

.upcoming-table td {
  padding: 10px 8px 10px 0;
  border-bottom: 1px solid #e6edf5;
  color: #2b4054;
}

.upcoming-table tr:last-child td {
  border-bottom: none;
}

@media (max-width: 640px) {
  .kpi-tile,
  .panel {
    flex: 1 1 100%;
  }
}
</style>
